import axios from 'axios';
import { useEffect, useState } from 'react';
import {
  Locations, SnappedPoint, LocationEntity,
} from './types';
import { AlertDetails } from '../../../API/types/Alerts';
import { LocationDetails } from '../../../API/types/Maps';
import {
  siftCoordinates, findNearestCoord,
} from './helpers';
import { chunkArray } from '../../../utils/arrUtils';
import { FeaturesManagement } from '../../../featuresControl';

export async function snapToRoad(coordinates: number[][], interpolate = true): Promise<SnappedPoint[]> {
  const endpoint = 'https://roads.googleapis.com/v1/snapToRoads';

  const path = coordinates.map((coord) => coord.join(',')).join('|');

  const response = await axios.get(
    `${endpoint}?path=${path}&key=${process.env.REACT_APP_GOOGLE_API_KEY}&interpolate=${interpolate}`,
  );

  return response.data.snappedPoints;
}

async function runSnapToRoad(coordinates: Locations): Promise<SnappedPoint[]> {
  if (!coordinates.length) return [];
  const rangedPoints = [...coordinates];
  rangedPoints.reverse();
  const snappedPoints = await Promise.all(chunkArray(rangedPoints, 100).map((coords) => snapToRoad(coords)));
  const snappedPath = snappedPoints
    .flat();
  snappedPath.reverse();
  return snappedPath;
}

export function useSnapToRoad(
  data: any,
  path: Locations,
  alerts: AlertDetails[],
  isTrip: boolean,
  roadMatching: boolean,
  alertsLoaded?: boolean,
  fakePoints?: boolean,
): [Locations, AlertDetails[], LocationDetails[]] {
  const [tripPoints, setTripPoints] = useState<Locations>(path);
  const [tripPointsOrigin, setTripPointsOrigin] = useState<LocationDetails[]>(data ?? []);
  const [alertsData, setAlertsData] = useState<AlertDetails[]>(alerts);

  useEffect((): void => {
    if (isTrip && path?.length && alertsLoaded) {
      if (!roadMatching) {
        setTripPoints(path);
        setTripPointsOrigin(data ?? []);
        setAlertsData(alerts);
      } else {
        const tripLocations = data?.map((item: LocationDetails): LocationEntity => ({
          id: item._id,
          location: [item?.location?.coordinates[1], item?.location?.coordinates[0]],
          createdAt: new Date(item.timestampSecEpoch * 1000).toString(),
        }));
        const alertsLocations = alerts
          ?.filter((alert) => alert?.lat && alert?.lon)
          ?.map((item: AlertDetails): LocationEntity => ({
            id: item._id,
            location: [item.lat as number, item.lon as number],
            createdAt: item.eventDate,
          }));

        const siftedCoordinates = tripLocations.length <= 100
          ? tripLocations
          : siftCoordinates(tripLocations);

        let locations = siftedCoordinates.concat(alertsLocations);
        locations = locations.sort((a: LocationEntity, b: LocationEntity) => (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ));

        const dataById = new Map<string, LocationDetails>();
        data?.forEach((item: LocationDetails) => dataById.set(item._id, item));

        runSnapToRoad(locations.map((item: LocationEntity) => item.location))
          .then((snappedPoints) => {
            const snappedPath = snappedPoints
              .map((snappedPoint) => [snappedPoint.location.latitude, snappedPoint.location.longitude]);
            setTripPoints(snappedPath);
            const snappedAlerts = alerts.map((alert) => {
              const nearest = findNearestCoord([alert.lat as number, alert.lon as number], snappedPath);
              return {
                ...alert,
                lat: nearest[0],
                lon: nearest[1],
              };
            });
            setAlertsData(snappedAlerts);

            if (FeaturesManagement.map.routePoints) {
              // Drag each real GPS point onto the closest vertex of the snapped
              // path we actually drew, keeping its original timestamp/speed so
              // the marker always sits on the rendered route.
              const snappedPathOrigin = siftedCoordinates
                .map((entity: LocationEntity): LocationDetails | null => {
                  const source = dataById.get(entity.id);
                  if (!source) return null;
                  const nearest = findNearestCoord(entity.location, snappedPath);
                  return {
                    ...source,
                    location: {
                      ...source.location,
                      coordinates: [nearest[1], nearest[0]],
                    },
                  };
                })
                .filter((point: LocationDetails | null): point is LocationDetails => point !== null);
              setTripPointsOrigin(snappedPathOrigin);
            }
          });
      }
    } else {
      setTripPoints(path);
      setTripPointsOrigin(data ?? []);
      setAlertsData(alerts);
    }
  }, [path, roadMatching, alertsLoaded]);

  return [tripPoints, alertsData, tripPointsOrigin];
}
